// Newspaper-style masthead shared by every page. Brief pages pass `date` +
// `generatedAt` to get the dateline row and thick-thin front-page rule;
// interior pages (Stay, About, the guides) render it bare and get the compact
// phone treatment below. The live front page also passes `ears`, the brief's
// tides, sun and moon, for the tide clock and the moon either side of the
// nameplate (MastheadEars).
import { Link } from 'react-router-dom';
import MastheadEars from './MastheadEars.jsx';
import {
  etDateOf,
  etToday,
  formatDate,
  formatDatelineShort,
  formatUpdatedAt
} from '../lib/format.js';

export default function Header({ date, generatedAt, ears }) {
  // Between midnight ET and the morning publish the live page shows
  // yesterday's brief; the lead's kicker says so (DailyScore), with when the
  // new one lands. It used to be said here as well, on its own line.
  const today = etToday();
  // Suffix only for exactly one day back; anything older trips the stale
  // banner, which says it better than a bare " yesterday" would.
  const refreshedEt = generatedAt ? etDateOf(generatedAt) : null;
  const refreshedYesterday =
    refreshedEt && new Date(today) - new Date(refreshedEt) === 86400000;
  // Every page gets a compact masthead on phones; desktop keeps the full one.
  // Interior pages went first: with the full mark, wordmark, tagline and nav,
  // the stay page's lead photo was a sliver at the bottom of a 390x844 first
  // screen and the calendar two screens down.
  //
  // The brief followed on 2026-09-27 (design review 2026-09-26, finding 3).
  // It used to keep its full masthead at every width, which ended at 277px of
  // a 390x664 first screen (42%) and pushed the first pick below the jump bar.
  // A returning guest opens this every morning and the nameplate does not
  // change between visits; the sense of an edition comes from the dateline and
  // the thick-thin rule, and both stay. The brief's phone wordmark stays a
  // touch larger than the interior pages' (28px against 24px), since it is
  // still the front page. Everything here keys off `date`, never the route.
  //
  // At sm+ a brief page's nameplate is 42px, a step over the 38px headline
  // under it, as a front page's flag should be; interior pages keep 36px.
  // Until 2026-10-02 both were 36px and the day's headline outweighed the
  // name of the paper.
  const interior = !date;
  return (
    <header>
      <div
        className={[
          'pt-2 text-center',
          ears ? 'min-[1120px]:grid min-[1120px]:grid-cols-[1fr_auto_1fr] min-[1120px]:items-center' : ''
        ].join(' ')}
      >
        <div className="min-[1120px]:col-start-2 min-[1120px]:row-start-1">
          {/* data-flag: the harbor chart measures this link to clear the band
              behind the nameplate on a narrow screen. */}
          <Link to="/" data-flag className="inline-block no-underline">
            {/* Palmetto + wave mark above the wordmark. Decorative (alt=""): the
                wordmark text below already names the brand. */}
            <img
              src="/logo-mark.svg"
              alt=""
              width="120"
              height="140"
              className={[
                'mx-auto w-auto sm:mb-1.5',
                interior ? 'mb-1.5 h-9 sm:h-14' : 'mb-1 h-8 sm:h-[60px]'
              ].join(' ')}
            />
            <span
              className={[
                'block font-display font-semibold tracking-tight text-navy-900',
                interior ? 'text-2xl sm:text-4xl' : 'text-[28px] leading-tight sm:text-[42px] sm:leading-[1.1]'
              ].join(' ')}
            >
              Charleston<span className="text-cream-500">.</span>Vacations
            </span>
          </Link>
          <p className="mt-1.5 hidden font-serif text-sm italic text-navy-500 sm:block">
            The daily brief for your Lowcountry stay
          </p>
        </div>
        {ears && <MastheadEars {...ears} />}
      </div>

      {/* "Stay" points at /stay, the host-neutral market index, NEVER at a
          property. That distinction is the whole reason it can live in shared
          chrome: commit 100199b removed a gold link that resolved to
          Object.keys(properties)[0], which made the entire publication an ad for
          one host. An index scales to any number of hosts and keeps the brief the
          city's asset. Do not "simplify" this into a deep link.

          Yesterday moved to the footer (2026-07-25, James: "the Yesterday link is
          useless"): it was spending a masthead slot on a page almost nobody wants
          while the booking surface had none. The route and footer link remain. */}
      {/* -my-3 py-3 on each link is not a no-op: 12px of tap padding above and
          below the 18px text (a 42px hit area on a phone) that the negative
          margins hand back, so the nav's own padding and hairlines stay put. */}
      <nav
        className={[
          'flex items-center justify-center gap-5 border-y border-navy-200 py-2.5 text-[12px] font-medium uppercase tracking-[0.16em] text-navy-600 sm:mt-5 sm:gap-6',
          interior ? 'mt-5' : 'mt-3'
        ].join(' ')}
      >
        <Link to="/" className="-my-3 py-3 no-underline hover:text-navy-900">
          Today
        </Link>
        <Link to="/guides" className="-my-3 py-3 no-underline hover:text-navy-900">
          Guides
        </Link>
        <Link to="/about" className="-my-3 py-3 no-underline hover:text-navy-900">
          About
        </Link>
        <Link
          to="/stay"
          className="group -my-3 py-3 text-cream-600 no-underline hover:text-navy-900"
        >
          {/* The gold hairline sits on an inline-block span, not the link: on
              the link the tap padding would push it 12px under the word. */}
          <span className="inline-block border-b border-cream-500 pb-0.5 group-hover:border-navy-900">
            Stay
          </span>
        </Link>
      </nav>

      {date && (
        <>
          {/* Short forms below sm, so the dateline is one row on a phone
              instead of two. flex-wrap stays as the fallback for the narrowest
              screens, where "yesterday" can still push it over. */}
          <div className="flex flex-wrap items-baseline justify-between gap-2 py-2.5 text-xs uppercase tracking-wider">
            <span className="font-medium text-navy-800">
              <span className="sm:hidden">{formatDatelineShort(date)}</span>
              <span className="hidden sm:inline">{formatDate(date)}</span>
            </span>
            <span className="normal-case tracking-normal text-navy-500">
              {/* "Conditions refreshed" keeps the rolling data stamp from
                  contradicting the footer's one-brief-by-6:30 promise. In
                  sentence case: the date beside it is the dateline, and a
                  second run of tracked capitals was just more to decode. */}
              <span className="sm:hidden">Refreshed {formatUpdatedAt(generatedAt, { zone: false })}</span>
              <span className="hidden sm:inline">Conditions refreshed {formatUpdatedAt(generatedAt)}</span>
              {refreshedYesterday ? ' yesterday' : ''}
            </span>
          </div>
          {/* Thick-thin newspaper rule under the dateline. */}
          <div className="border-t-2 border-navy-900" />
          <div className="mt-[3px] border-t border-navy-900" />
        </>
      )}
    </header>
  );
}
